import {
  Box,
  Card,
  CardContent,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import NavBar from '../../components/NavBar/NavBar';
import BolPrint from '../../components/Print/BolPrint';
import { URL_PREFIX } from '../../constants/stores';
import { getDayOfWeek } from '../../Utils/getDaybyDate';

const TodayList: React.FC = () => {
  const [bolList, setBolList] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 1,
  });

  const token = useSelector((state: any) => state.user.sessionId);
  const user = useSelector((state: any) => state.user.user);

  const columns = useMemo(
    () => [
      { accessorKey: 'index', header: 'No.' },
      { accessorKey: 'shipper', header: 'Shipper' },
      { accessorKey: 'receiver', header: 'Receiver' },
      { accessorKey: 'po_no', header: 'PO No.' },
      { accessorKey: 'bol_date', header: 'BOL Date', width: 120 },
      { accessorKey: 'amount', header: 'Rate' },
      { accessorKey: 'type', header: 'Load/Unload' },
      { accessorKey: 'bol_no', header: 'BOL No.' },
      { accessorKey: 'trailer', header: 'Carrier' },
    ],
    []
  );

  const initialFilter = {
    shipper: '',
    receiver: '',
    po_no: '',
    bol_no: '',
    type: '',
    invoice_no: '',
    vendor_delivered: null as boolean | null,
    dc_received: null as boolean | null,
    approved: null as boolean | null,
    payment_status: null as boolean | null,
    trailer: '',
    supplier: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
  };

  const [filter] = useState(initialFilter);
  const [dcLocation] = useState<any>(user.location);
  const [trigger] = useState(false);

  useEffect(() => {
    getBolList(pagination.page, pagination.limit);
  }, [pagination.page, trigger]);

  const getBolList = async (page: number, limit: number) => {
    try {
      const url =
        user.category === 'CLIENT'
          ? `${URL_PREFIX}/bol/getBolList`
          : `${URL_PREFIX}/bol/getBolListG2000`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          page,
          limit,
          dclocation: dcLocation,
          ...filter,
        }),
      });

      const data = await response.json();

      setBolList(data.data || []);
      setPagination((prev) => ({
        ...prev,
        page,
        total: Math.ceil((data.totalRows || 0) / prev.limit) || 1,
      }));
    } catch (error) {
      console.log('Error in getBolList:', error);
    }
  };

  const handlePagination = (operation: 'increase' | 'decrease') => {
    setPagination((prev) => {
      if (operation === 'increase') {
        return {
          ...prev,
          page: prev.page === prev.total ? prev.page : prev.page + 1,
        };
      }

      return {
        ...prev,
        page: prev.page === 1 ? prev.page : prev.page - 1,
      };
    });
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <>
      <NavBar />

      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              Daily Dispatches
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {today} ({getDayOfWeek(new Date())})
            </Typography>
          </Box>

          <Card>
            <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
              <TableContainer component={Paper} variant="outlined">
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {columns.map((column) => (
                        <TableCell
                          key={column.accessorKey}
                          sx={{ width: column.width || 'auto' }}
                        >
                          {column.header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {bolList.map((bol: any, index: number) => {
                      const isComplete =
                        bol?.dc_received &&
                        bol?.vendor_delivered &&
                        bol?.payment_status;

                      return (
                        <TableRow
                          key={bol.id}
                          hover
                          sx={{
                            backgroundColor: bol.cancel
                              ? '#c1121f'
                              : bol.payment_status
                                ? '#C9E6F0'
                                : undefined,
                            '& td': {
                              color: bol.cancel ? '#fff' : 'inherit',
                            },
                          }}
                        >
                          <TableCell>
                            {index + 1 + (pagination.page - 1) * pagination.limit}
                          </TableCell>

                          <TableCell>
                            {bol.shipper?.name}(
                            {bol.shipper?.postal_code?.split(',')[0]})
                          </TableCell>

                          <TableCell>
                            {bol.receiver?.name}(
                            {bol.receiver?.postal_code?.split(',')[0]})
                          </TableCell>

                          <TableCell>
                            {bol.po?.map((po: any) => po.po_no).join('/ ')}
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2" sx={{ minWidth: 90 }}>
                              {bol.bol_date?.split('T')[0] ?? ''}
                            </Typography>
                            <Typography variant="caption" fontWeight={700}>
                              {getDayOfWeek(bol.bol_date) ?? ''}
                            </Typography>
                          </TableCell>

                          <TableCell>{`$${bol?.amount ?? ''}`}</TableCell>

                          <TableCell>{bol?.type}</TableCell>

                          <TableCell sx={{ fontWeight: 700 }}>
                            <BolPrint
                              bol={bol}
                              size="xs"
                              variant="outline"
                              textElement={bol?.bol_no}
                            />
                          </TableCell>

                          <TableCell>{bol?.trailer}</TableCell>
                        </TableRow>
                      );
                    })}

                    {!bolList.length && (
                      <TableRow>
                        <TableCell colSpan={columns.length} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No dispatches found for today.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Stack>
      </Box>
    </>
  );
};

export default TodayList;